import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The Security page.
 *
 * One form, three sections, and the thresholds are read from the rows the
 * server rendered rather than from a list held here: the kinds come from the
 * model, and a fifth one added tomorrow would be saved by this file without a
 * line changing.
 */

interface Threshold {
  kind: string;
  limit: number;
  windowSeconds: number;
}

interface Settings {
  gate: {
    enabled: boolean;
    minAccountDays: number;
    requireAvatar: boolean;
    namePatterns: string[];
    blockStaffBots: boolean;
    action: string;
    quarantineRoleId: string;
    alertChannelId: string;
  };
  antinuke: {
    enabled: boolean;
    simulate: boolean;
    thresholds: Threshold[];
    exemptUserIds: string[];
    exemptRoleIds: string[];
    alertChannelId: string;
  };
}

export function initSecurityPage(): void {
  const form = $<HTMLFormElement>('[data-security-form]');
  if (!form) return;

  const { settings, guildId } = pageData<{ settings: Settings; guildId: string }>();

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/security?guildId=${guildId}`, {
        guildId,
        gate: {
          enabled: checkbox(form, 'gateEnabled'),
          minAccountDays: number(form, 'minAccountDays'),
          requireAvatar: checkbox(form, 'requireAvatar'),
          /*
           * One fragment per line, empty lines dropped.
           *
           * A textarea is the only widget where somebody types a list without
           * being taught a syntax, and the empty line at the end is what every
           * one of them leaves behind.
           */
          namePatterns: text(form, 'namePatterns')
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length >= 2)
            .slice(0, 20),
          blockStaffBots: checkbox(form, 'blockStaffBots'),
          action: select(form, 'action'),
          quarantineRoleId: findSelectMenu(form, 'quarantineRoleId')?.single ?? '',
          alertChannelId: findSelectMenu(form, 'gateAlertChannelId')?.single ?? '',
        },
        antinuke: {
          enabled: checkbox(form, 'nukeEnabled'),
          simulate: checkbox(form, 'simulate'),
          thresholds: thresholds(),
          exemptUserIds: settings.antinuke.exemptUserIds,
          exemptRoleIds: findSelectMenu(form, 'exemptRoleIds')?.value ?? [],
          alertChannelId: findSelectMenu(form, 'nukeAlertChannelId')?.single ?? '',
        },
      });
    },
  });

  const quarantine = findSelectMenu(form, 'quarantineRoleId');
  if (quarantine)
    quarantine.value = settings.gate.quarantineRoleId ? [settings.gate.quarantineRoleId] : [];

  const gateAlert = findSelectMenu(form, 'gateAlertChannelId');
  if (gateAlert)
    gateAlert.value = settings.gate.alertChannelId ? [settings.gate.alertChannelId] : [];

  const nukeAlert = findSelectMenu(form, 'nukeAlertChannelId');
  if (nukeAlert)
    nukeAlert.value = settings.antinuke.alertChannelId ? [settings.antinuke.alertChannelId] : [];

  const exempt = findSelectMenu(form, 'exemptRoleIds');
  if (exempt) exempt.value = settings.antinuke.exemptRoleIds;
}

/** Read from the rendered rows, so the kinds stay the model's business. */
function thresholds(): Threshold[] {
  return $$<HTMLElement>('[data-threshold]').map((row) => {
    const kind = row.dataset.threshold ?? '';
    const inputs = row.querySelectorAll<HTMLInputElement>('input[type="number"]');

    return {
      kind,
      limit: Number(inputs[0]?.value) || 3,
      windowSeconds: Number(inputs[1]?.value) || 60,
    };
  });
}

function number(form: HTMLFormElement, name: string): number {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? Number(field.value) || 0 : 0;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLTextAreaElement ? field.value : '';
}

function select(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLSelectElement ? field.value : '';
}
